import {
  Alert,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Field,
  FieldError,
  FieldHint,
  Input,
  Label,
  StatusPill,
  cn,
} from "@breenova/ui";
import { Laptop } from "lucide-react";
import { useState, type FormEvent } from "react";

import { DetailHeader } from "@/components/app-shell";
import { useChangePassword, useRevokeSession, useSessions, type SessionItem } from "@/lib/account";
import { SettingsFrame } from "./settings-frame";

/** Mirrors the server's policy, so a short password is caught before the round trip. */
const MINIMUM_PASSWORD_LENGTH = 12;

/**
 * Password and signed-in devices.
 *
 * The two belong on one screen because they answer the same question — "someone else may
 * have got in, what do I do about it?" — and the answer is usually both at once.
 */
export function SecurityPage() {
  return (
    <div className="flex flex-col gap-md md:gap-lg">
      <DetailHeader backTo="/more" kicker="Your aviary" title="Security" />

      <SettingsFrame>
        <section className="flex w-full flex-col gap-md">
          <ChangePasswordCard />
          <SessionsCard />
        </section>
      </SettingsFrame>
    </div>
  );
}

function ChangePasswordCard() {
  const change = useChangePassword();

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [done, setDone] = useState<number | null>(null);

  const tooShort = newPassword.length > 0 && newPassword.length < MINIMUM_PASSWORD_LENGTH;
  const mismatched = confirmation.length > 0 && confirmation !== newPassword;

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setDone(null);

    if (tooShort || mismatched || !currentPassword) {
      return;
    }

    const ended = await change.mutateAsync({ currentPassword, newPassword });

    setCurrentPassword("");
    setNewPassword("");
    setConfirmation("");
    setDone(ended);
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Password</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={(event) => void handleSubmit(event)} className="flex flex-col gap-md" noValidate>
          {change.isError ? (
            <Alert tone="error">
              {change.error instanceof Error
                ? change.error.message
                : "Could not change your password."}
            </Alert>
          ) : null}

          {done !== null ? (
            <Alert tone="success">
              {done === 0
                ? "Password changed. No other devices were signed in."
                : `Password changed, and ${done === 1 ? "1 other device was" : `${done} other devices were`} signed out.`}
            </Alert>
          ) : null}

          <Field>
            <Label htmlFor="currentPassword">Current password</Label>
            <Input
              id="currentPassword"
              name="currentPassword"
              type="password"
              autoComplete="current-password"
              required
              value={currentPassword}
              onChange={(event) => {
                setCurrentPassword(event.target.value);
                setDone(null);
              }}
            />
          </Field>

          <Field>
            <Label htmlFor="newPassword">New password</Label>
            <Input
              id="newPassword"
              name="newPassword"
              type="password"
              autoComplete="new-password"
              required
              minLength={MINIMUM_PASSWORD_LENGTH}
              value={newPassword}
              onChange={(event) => {
                setNewPassword(event.target.value);
                setDone(null);
              }}
              aria-invalid={tooShort ? true : undefined}
              aria-describedby="new-password-hint"
            />
            {tooShort ? (
              <FieldError>At least {MINIMUM_PASSWORD_LENGTH} characters.</FieldError>
            ) : (
              <FieldHint id="new-password-hint">
                At least {MINIMUM_PASSWORD_LENGTH} characters. A few ordinary words beat one
                cryptic one, and they are far easier to type in a cold birdroom.
              </FieldHint>
            )}
          </Field>

          <Field>
            <Label htmlFor="confirmation">Repeat new password</Label>
            <Input
              id="confirmation"
              name="confirmation"
              type="password"
              autoComplete="new-password"
              required
              value={confirmation}
              onChange={(event) => {
                setConfirmation(event.target.value);
                setDone(null);
              }}
              aria-invalid={mismatched ? true : undefined}
            />
            {mismatched ? <FieldError>These two do not match.</FieldError> : null}
          </Field>

          <p className="text-body text-on-surface-variant">
            Changing your password signs out every other device. This one stays signed in.
          </p>

          <Button
            type="submit"
            className="self-start"
            disabled={change.isPending || tooShort || mismatched || !currentPassword || !newPassword}
          >
            {change.isPending ? "Changing…" : "Change password"}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

function SessionsCard() {
  const sessions = useSessions();
  const revoke = useRevokeSession();

  const others = (sessions.data ?? []).filter((session) => !session.isCurrent);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Signed-in devices</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-md">
        {revoke.isError ? (
          <Alert tone="error">
            {revoke.error instanceof Error ? revoke.error.message : "Could not end that session."}
          </Alert>
        ) : null}

        <p className="text-body text-on-surface-variant">
          A sign-in lasts thirty days. If one of these is a phone you no longer have, end it
          here — and change your password too, since whoever holds it can sign in again.
        </p>

        {sessions.isPending ? (
          <p className="text-body text-on-surface-variant">Loading your devices…</p>
        ) : sessions.isError ? (
          <Alert tone="error">Could not load your signed-in devices.</Alert>
        ) : (
          <ul className="flex flex-col">
            {(sessions.data ?? []).map((session) => (
              <SessionRow
                key={session.id}
                session={session}
                busy={revoke.isPending}
                onRevoke={() => revoke.mutate(session.id)}
              />
            ))}
          </ul>
        )}

        {others.length > 0 ? (
          <Button
            variant="secondary"
            className="self-start"
            disabled={revoke.isPending}
            onClick={() => revoke.mutate(undefined)}
          >
            {revoke.isPending
              ? "Signing out…"
              : `Sign out ${others.length === 1 ? "the other device" : `all ${others.length} other devices`}`}
          </Button>
        ) : null}
      </CardContent>
    </Card>
  );
}

function SessionRow({
  session,
  busy,
  onRevoke,
}: {
  session: SessionItem;
  busy: boolean;
  onRevoke: () => void;
}) {
  return (
    <li
      className={cn(
        "flex flex-wrap items-center gap-sm py-sm",
        "border-b border-outline-variant last:border-b-0",
      )}
    >
      <Laptop className="size-5 shrink-0 text-on-surface-variant" aria-hidden />

      <div className="flex min-w-0 flex-1 flex-col gap-xs">
        <span className="flex flex-wrap items-center gap-sm">
          <span className="truncate text-body text-on-surface">
            {session.device ?? "Unnamed device"}
          </span>
          {session.isCurrent ? <StatusPill tone="positive">This device</StatusPill> : null}
        </span>

        <span className="text-data text-on-surface-variant">
          Last used {relativeTime(session.lastSeenAt)} · signed in {formatDate(session.startedAt)}
        </span>
      </div>

      {session.isCurrent ? null : (
        <Button variant="secondary" size="sm" disabled={busy} onClick={onRevoke}>
          Sign out
        </Button>
      )}
    </li>
  );
}

/**
 * Converts a stored UTC instant to the device's own local date.
 *
 * The presentation edge, per the storage rule: everything above this line is UTC.
 */
function formatDate(utc: string): string {
  return new Date(utc).toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

/**
 * "3 hours ago" rather than a timestamp.
 *
 * The only question this line answers is "was that me, just now?", and a wall-clock time
 * makes the reader do the subtraction themselves.
 */
function relativeTime(utc: string): string {
  const minutes = Math.round((Date.now() - new Date(utc).getTime()) / 60_000);

  if (minutes < 1) {
    return "just now";
  }

  const formatter = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });

  if (minutes < 60) {
    return formatter.format(-minutes, "minute");
  }

  const hours = Math.round(minutes / 60);

  if (hours < 24) {
    return formatter.format(-hours, "hour");
  }

  return formatter.format(-Math.round(hours / 24), "day");
}
